/**
 * The flock, and it is three draw calls.
 *
 * ── Why this is not eight `hen.create()` calls ──────────────────────────────
 *
 * The fattening hall's budget row is the tightest in the game and beat 8 puts
 * Kestner in it, who costs 32 draw calls on his own (measured, session 35). Eight
 * birds built the obvious way — one group each, ten meshes each, three passes
 * each — is upwards of two hundred. Eight birds sharing one `InstancedMesh` per
 * surface is three, whatever the flock size.
 *
 * That is why `mergeBySurface` was split out of `instanceGroup`: the merge is
 * exactly what was wanted and the baked placements are exactly what was not.
 * Every bird's transform is written into the per-instance matrix each frame.
 *
 * ── What that costs, stated rather than discovered later ────────────────────
 *
 * **The hens have no moving parts.** Head, wings and legs are merged into the
 * body, so a hen cannot flap, and a panicking bird that does not flap is a real
 * loss. Two candidate ways out were considered and both were rejected:
 *
 *   - A separate instanced mesh for the wings costs two more batches (left and
 *     right differ by more than a mirror, and a mirrored instance flips its
 *     winding). Six more draw calls to flap.
 *   - Merging both wings into one rigid piece and rolling it is not a flap, it
 *     is a roll, and it reads as a bird being tipped over.
 *
 * So the panic is carried entirely by the root transform: a fast, uneven gait, a
 * yaw that keeps changing its mind, and a short low hop. A hen that hops reads as
 * a bird; that turned out to be enough, and it was checked in a frame rather
 * than assumed.
 *
 * ── Nothing here is simulation ──────────────────────────────────────────────
 *
 * A hen has no collider, no stealth presence and no consequence. Kestner does not
 * see them, the player cannot pick them up, and the beat does not read their
 * positions. They are dressing that moves, so this runs on the render step with
 * the other directors — but every random number comes from `mulberry32` off a
 * fixed seed (D-045), so two runs of the same beat put the same bird in the same
 * corner.
 */

import * as THREE from 'three';
import { mulberry32 } from '@/audio/prng';
import { getAsset } from '@/models/registry';
import { mergeBySurface } from '@/world/instancing';
import { BLACKOUT_OPEN_CRATES, FAT_HALL } from '@/world/fattening';
import { BlackoutCrates } from '@/world/blackoutCrates';

/** Four to a crate, which is what a transport crate is actually stocked at. */
export const HENS_PER_CRATE = 4;
export const FLOCK_SIZE = BLACKOUT_OPEN_CRATES.length * HENS_PER_CRATE;

/** D-045: the beat replays. Any literal here would be a bird that moves differently twice. */
const FLOCK_SEED = 0x48454e53;

/** Half the clear alley, less a bird's width, so nothing walks inside the pen wall. */
const ALLEY_LIMIT = FAT_HALL.alley - 0.18;
/** The pen run stops at ±10.8; the cross-passages beyond it are where the doors are. */
const RUN_LIMIT = 10.4;

/** A panicking broiler is fast and cannot keep it up. Both halves matter. */
const RUN_SPEED = 1.9;
const HOP_PERIOD = 0.42;
const HOP_HEIGHT = 0.055;

interface Hen {
  x: number;
  z: number;
  facing: number;
  /** Seconds until this bird leaves the crate. Staggered — they do not pour out as one. */
  delay: number;
  /** Seconds of running left before it gives up. This is "uselessly", as a number. */
  stamina: number;
  hop: number;
  /** How hard this bird is currently swerving. Rebuilt on every wall it meets. */
  turn: number;
  out: boolean;
}

export class HenFlock {
  readonly group: THREE.Group;
  readonly #meshes: THREE.InstancedMesh[] = [];
  readonly #hens: Hen[] = [];
  readonly #random: () => number;
  /** Openable-crate index → whether its birds have been let go. */
  readonly #released: boolean[];

  constructor() {
    const asset = getAsset('hen');
    if (!asset) throw new Error('hen flock: hen asset is not registered');

    this.group = new THREE.Group();
    this.group.name = 'blackout-hen-flock';
    this.#random = mulberry32(FLOCK_SEED);
    this.#released = BLACKOUT_OPEN_CRATES.map(() => false);

    for (const batch of mergeBySurface(asset.create())) {
      const mesh = new THREE.InstancedMesh(batch.geometry, batch.material, FLOCK_SIZE);
      mesh.name = `blackout-hen-${batch.name}`;
      mesh.castShadow = batch.castShadow;
      mesh.receiveShadow = batch.receiveShadow;
      // The birds move, so a bounding sphere computed once is a lie by the second
      // frame. Culling a flock of eight in a corridor buys nothing worth the risk
      // of the whole flock blinking out when the camera turns.
      mesh.frustumCulled = false;
      this.#meshes.push(mesh);
      this.group.add(mesh);
    }

    for (let index = 0; index < FLOCK_SIZE; index += 1) {
      const crate = Math.floor(index / HENS_PER_CRATE);
      const mouth = BlackoutCrates.mouthOf(crate);
      this.#hens.push({
        x: mouth.x,
        z: mouth.z,
        facing: 0,
        // 0…0.9 s apart. Birds leave a crate one at a time because the mouth is
        // one bird wide, and the stagger is the whole difference between a flock
        // and a puff of chickens.
        delay: (index % HENS_PER_CRATE) * 0.22 + this.#random() * 0.14,
        // Between two and seven seconds. The short end is not a bird that fails;
        // it is a bird that stops in the middle of an alley and stands there,
        // which is the honest picture of what being freed into a shed is worth.
        stamina: 2 + this.#random() * 5,
        hop: this.#random(),
        turn: 0,
        out: false,
      });
    }

    this.#writeMatrices();
  }

  /** Let one crate's birds go. Called when its lid has finished swinging. */
  release(openableIndex: number): void {
    if (this.#released[openableIndex] !== false) return;
    this.#released[openableIndex] = true;
    const mouth = BlackoutCrates.mouthOf(openableIndex);
    for (let index = 0; index < HENS_PER_CRATE; index += 1) {
      const hen = this.#hens[openableIndex * HENS_PER_CRATE + index];
      if (!hen) continue;
      hen.x = mouth.x;
      hen.z = mouth.z;
      // ── Down the hall, not across it ────────────────────────────────────
      //
      // The first build sent them out of the crate mouth pointing across the
      // alley (+x) with a narrow fan. The alley is 1.84 m of clear width and
      // twenty metres long, so every bird crossed it in under a second, bounced,
      // crossed back, and the flock stayed in a huddle at the crate it came out
      // of. On paper that is "scatter uselessly"; in the frame it reads as four
      // birds refusing to leave, which is a different sentence.
      //
      // So the heading is drawn along ±Z — the direction the room actually has —
      // with a fan wide enough that they still find the side walls. They spread
      // the length of the hall and the player cannot herd them, which is the
      // scene spec §7.8 asks for.
      hen.facing = (this.#random() < 0.5 ? 0 : Math.PI) + (this.#random() - 0.5) * 1.5;
    }
  }

  /** How many birds are out of a crate and on the floor. For the gate, not for the player. */
  get freed(): number {
    return this.#hens.filter((hen) => hen.out).length;
  }

  /** True once every freed bird has run itself out. The crate stage waits on this. */
  get settled(): boolean {
    const released = this.#released.some((flag) => flag);
    return released && this.#hens.every((hen) => !hen.out || hen.stamina <= 0);
  }

  update(frameDt: number): void {
    const dt = Number.isFinite(frameDt) ? Math.max(0, Math.min(0.1, frameDt)) : 0;
    if (dt === 0) return;

    let anyReleased = false;
    for (let index = 0; index < this.#hens.length; index += 1) {
      const hen = this.#hens[index];
      if (!hen) continue;
      const crate = Math.floor(index / HENS_PER_CRATE);
      if (this.#released[crate] !== true) continue;
      anyReleased = true;

      if (hen.delay > 0) {
        hen.delay -= dt;
        continue;
      }
      hen.out = true;
      if (hen.stamina <= 0) continue;

      hen.stamina -= dt;
      // The swerve decays and is topped back up at every wall, so a bird runs
      // straighter the longer it has been running and jinks hardest right after
      // it has just found out that the room has an edge.
      hen.turn *= Math.exp(-2.4 * dt);
      hen.facing += hen.turn * dt + (this.#random() - 0.5) * 0.8 * dt;

      // Speed falls away with what is left of the panic rather than stopping on
      // a switch: a bird that halts mid-stride reads as a dropped frame.
      const effort = Math.max(0, Math.min(1, hen.stamina / 1.6));
      const speed = RUN_SPEED * effort;
      hen.x += Math.sin(hen.facing) * speed * dt;
      hen.z += Math.cos(hen.facing) * speed * dt;

      if (hen.x < -ALLEY_LIMIT || hen.x > ALLEY_LIMIT) {
        hen.x = Math.max(-ALLEY_LIMIT, Math.min(ALLEY_LIMIT, hen.x));
        // Reflect about the wall's normal — sin flips, cos does not — plus a
        // kick, because a bird hitting a wall in the dark does not bounce
        // cleanly off it.
        hen.facing = -hen.facing;
        hen.turn = (this.#random() - 0.5) * 7;
      }
      if (hen.z < -RUN_LIMIT || hen.z > RUN_LIMIT) {
        hen.z = Math.max(-RUN_LIMIT, Math.min(RUN_LIMIT, hen.z));
        hen.facing = Math.PI - hen.facing;
        hen.turn = (this.#random() - 0.5) * 7;
      }

      hen.hop += (dt / HOP_PERIOD) * (0.5 + effort);
    }

    if (anyReleased) this.#writeMatrices();
  }

  #writeMatrices(): void {
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const euler = new THREE.Euler(0, 0, 0, 'YXZ');
    const one = new THREE.Vector3(1, 1, 1);

    for (let index = 0; index < this.#hens.length; index += 1) {
      const hen = this.#hens[index];
      if (!hen) continue;

      if (!hen.out) {
        // Still in the crate, and a crate interior is 155 mm tall — a 410 mm hen
        // does not fit in one, and squashing the transform to make it fit would
        // be a lie told in a matrix. Birds that have not come out are not drawn.
        // The four crates she cannot open are heard and never seen, which is the
        // stronger picture anyway.
        matrix.makeScale(0, 0, 0);
        for (const mesh of this.#meshes) mesh.setMatrixAt(index, matrix);
        continue;
      }

      const running = hen.stamina > 0;
      const phase = hen.hop * Math.PI * 2;
      // The hop is a half-sine clipped at the floor: airborne for part of the
      // cycle and flat-footed for the rest, which is a gait rather than a float.
      const lift = running ? Math.max(0, Math.sin(phase)) * HOP_HEIGHT : 0;
      position.set(hen.x, lift, hen.z);
      // Pitched forward while running and level once it has stopped. A standing
      // broiler is upright; a running one has its whole body over its feet.
      euler.set(running ? -0.22 - lift * 1.4 : 0, hen.facing, running ? Math.cos(phase) * 0.09 : 0);
      quaternion.setFromEuler(euler);
      matrix.compose(position, quaternion, one);
      for (const mesh of this.#meshes) mesh.setMatrixAt(index, matrix);
    }

    for (const mesh of this.#meshes) mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    for (const mesh of this.#meshes) {
      mesh.geometry.dispose();
      mesh.removeFromParent();
    }
    this.#meshes.length = 0;
    this.group.removeFromParent();
  }
}
